import {createElement as h} from "react";

import {cst, cstAssign}         from "@Common/react/chain-services";
import {consumeContext}         from "@Common/react/context";
import {Invoke}                 from "@Common/react/invoke";
import {stateChangeNestedValue} from "@Common/react/state-change-nested-value";
import {Static2}                from "@Common/react/static-2";
import {StorageState}           from "@Common/react/storage-state";
import {UseEffect}              from "@Common/react/use-effect";
import {Types}                  from "@Common/types";
import {entries, filterValues, mapValues}     from "@Common/utils/objects";

import {COMPANY_LEVEL} from "@Xpresso/routes/common/logic/user-level";

import {makeAsyncQueue}                   from "./async-queue";
import {makeDeliveryTypeModule}           from "./reports/delivery-type";
import {makeTopTenAgentsModule}           from "./reports/top-ten-agents";
import {makeTopTenOfficesModule}          from "./reports/top-ten-offices";
import {makeTotalSpendModule}             from "./reports/total-spend";
import {makeTotalSpendByProductModule}    from "./reports/total-spend-by-product";
import {makeTotalSpendByProgramModule}    from "./reports/total-spend-by-program";
import {makeTransactionInformationModule} from "./reports/transaction-information";
import {makeUserRegistrationModule}       from "./reports/user-registration";

type ReportModule           = Types.Xpresso.Account.Admin.Reporting.ActivityDashboard.ReportModule;
type ReportModuleLoadResult = Types.Xpresso.Account.Admin.Reporting.ActivityDashboard.ReportModuleTabResult[];

const REPORT_MODULE_MAKERS:Types.Xpresso.Account.Admin.Reporting.ActivityDashboard.ReportModuleFactory[] = [
    makeDeliveryTypeModule,
    makeTopTenAgentsModule,
    makeTopTenOfficesModule,
    makeTotalSpendByProductModule,
    makeTotalSpendByProgramModule,
    makeTotalSpendModule,
    makeTransactionInformationModule,
    makeUserRegistrationModule,
];

const START_OF_DAY_TIMESTAMP = (():number => {
    const today = new Date;
    return new Date(today.getFullYear(), today.getMonth(), today.getDate()).valueOf();
})();

/**
 * Resolves report modules
 */
export const ReportModuleLoader = ({key, next: rootNext, reportParamsMap}:{
    key             : number | string,
    next            : (v:Types.Xpresso.Account.Admin.Reporting.ActivityDashboard.ReportModuleLoadsStateNext) => JSX.Element,
    reportParamsMap : {[reportName: string]: any},
}) => cst<{
    apis                : Types.Apis.Loaders.XpressoLoadApis,
    cacheState          : Types.Common.React.StorageState.Next<{
        [reportName:string]: {
            [paramsKey:string]: {
                loaded    : ReportModuleLoadResult,
                timestamp : number,
            }
        }
    }>,
    loadsStatic         : Types.Common.React.Static2.Static2Next<{
        [reportName:string]: {
            [paramsKey:string]: Promise<ReportModuleLoadResult>,
        }
    }>,
    reportModulesStatic : Types.Common.React.Static2.Static2Next<{[moduleName:string]: ReportModule}>,
    userLevels          : Types.Apis.Loaders.LoadUserLevelsNext,
}>(
    consumeContext("apis"),
    consumeContext("userLevels"),
    cstAssign("cacheState", ({}, next) => StorageState({next, defaultValue: {}, key: makeStorageKey({key}), storage: window.localStorage})),
    cstAssign("loadsStatic", ({cacheState}, next) => (
        Static2({
            next,
            getInitValue: () => (
                mapValues(cacheState.value ?? {}, (paramsCache) => (
                    mapValues(
                        filterValues(paramsCache ?? {}, Boolean),
                        ({loaded}) => Promise.resolve(loaded)
                    )
                ))
            )
        })
    )),
    cstAssign("reportModulesStatic", ({apis, userLevels: {userLevel}}, next) => (
        Static2({
            next,
            getInitValue: () => Object.fromEntries(
                REPORT_MODULE_MAKERS
                    .map(makeModule => makeModule({apis}))
                    .filter(module => userLevel === COMPANY_LEVEL || module.supportsBranchLevel)
                    .map(module => [module.name, module])
            )
        })
    )),
    ({cacheState, loadsStatic: {get: getLoads}, reportModulesStatic: {get: getReportModules}}, next) => (
        h(UseEffect, {next, fn: () => {
            const loads = getLoads();
            for (let reportEntry of entries(getReportModules())) {
                const [reportName, report] = reportEntry;
                const paramsKey            = reportParamsMap?.[reportName] ? mapParamsAsKey(reportParamsMap[reportName]) : null;
                const inCache              = Boolean(cacheState.value?.[reportName]?.[paramsKey]);
                const inSelection          = paramsKey !== null;
                const inLoads              = Boolean(loads[reportName]?.[paramsKey]);

                if (!loads[reportName]) {
                    loads[reportName] = {};
                }

                if (!inSelection) {
                    delete loads[reportName]?.[paramsKey];
                } else if (inCache) {
                    loads[reportName][paramsKey] = Promise.resolve(cacheState.value[reportName][paramsKey].loaded);
                } else if (!inLoads) {
                    const load = loads[reportName][paramsKey] = enqueueAsync(report.load, [reportParamsMap[reportName]]);
                    load.then(
                        (loaded) => stateChangeNestedValue(
                            cacheState,
                            [reportName, paramsKey],
                            () => ({loaded, timestamp: Date.now()}),
                        )
                    );
                }
            }
        }})
    ),
    ({cacheState, loadsStatic: {get: getLoads}}, next) => (
        Invoke({next, fn: () => {
            // clear expired. set default
            const loads = getLoads();
            for (let reportName in (cacheState.value ?? {})) {
                for (let paramsKey in (cacheState.value[reportName] ?? {})) {
                    const isEmpty = !cacheState.value[reportName][paramsKey]?.timestamp;
                    const isOld   = !isEmpty && cacheState.value[reportName][paramsKey].timestamp < START_OF_DAY_TIMESTAMP;
                    if (isEmpty || isOld) {
                        if (loads[reportName]?.[paramsKey]) {
                            delete loads[reportName][paramsKey];
                        }

                        stateChangeNestedValue(cacheState, [reportName, paramsKey], () => null);
                    }
                }
            }
        }})
    ),
    ({cacheState: {onChange: setCache, value: cache}, loadsStatic: {set: setLoads}, reportModulesStatic: {get: getReports}}) => rootNext({
        getOptions : () => entries(getReports()).map(([name, {supportsBranchLevel=false, title}]) => ({name, supportsBranchLevel, title})),
        get        : (reportName:string) => {
            const paramsKey = mapParamsAsKey(reportParamsMap[reportName]);
            return {...getReports()[reportName], ...cache?.[reportName]?.[paramsKey]};
        },
        reload     : () => {
            setLoads({});
            setCache({});
        },
    }),
);

const {add: enqueueAsync} = makeAsyncQueue();

const makeStorageKey = ({key}:{key: number | string}) => `activity-dashboard-report-loads-d994cd2454ebf250-${key}`;

const mapParamsAsKey = JSON.stringify;

